import hotkeys from 'hotkeys-js';
import {Canvas, FabricImage, Point, util} from '@postermywall/fabricjs-2';
import type {CanvasEvents, FabricObject, TPointerEventInfo} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {CropData} from '@PosterWhiteboard/classes/item-crop.class';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import type {RefinedCutoutSources, RefinementMasks, RefinementPoint} from '@Libraries/background-refinement.library';
import {
  compositeRefinedCutout,
  compositeRefinedCutoutForPreview,
  countRefinementPoints,
  createEmptyRefinementMasks,
  createRefinementStroke,
  getRefinementMaskSize,
  hasRestoreStrokes,
  paintRefinementSegment,
  rasterizeRefinement,
  REFINEMENT_MAX_POINTS,
} from '@Libraries/background-refinement.library';
import type {ItemRefinementObject, ItemRefinementStrokeObject} from '@PosterWhiteboard/classes/item-refinement.types';
import {doesSupportRemoveBackground, isUserUploadedImageSource} from '@Libraries/image-item.library';
import {uploadCanvasAsUserImage} from '@Libraries/user-media-library';
import {loadImageWithRetriesAsync, withCanvasAsImageAsync} from '@Utils/image.util';
import {degreesToRadians} from '@Utils/math.util';
import {noop} from '@Utils/general.util';
import {getPointerClientPoint} from '@PosterWhiteboard/libraries/pointer-client-point.library';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import clamp from 'lodash/clamp';
import isEqual from 'lodash/isEqual';
import type {CutoutRefineModeSnapshot, CutoutRefineOpenRequest, CutoutRefineTargetItem} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine.types';
import {PosterCutoutRefineAnalytics} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine-analytics.class';
import {
  CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_COLOR,
  CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_PX,
  CUTOUT_REFINE_BRUSH_CURSOR_RING_COLOR,
  CUTOUT_REFINE_BRUSH_CURSOR_RING_PX,
  CUTOUT_REFINE_BRUSH_CURSOR_Z_INDEX,
  CUTOUT_REFINE_CHECKER_DARK,
  CUTOUT_REFINE_CHECKER_LIGHT,
  CUTOUT_REFINE_CHECKER_SQUARE_SCREEN_PX,
  CUTOUT_REFINE_DEFAULT_BRUSH_SIZE,
  CUTOUT_REFINE_FLATTENED_FILE_SUFFIX,
  CUTOUT_REFINE_MAX_BRUSH_SIZE,
  CUTOUT_REFINE_MAX_UNDO_STEPS,
  CUTOUT_REFINE_MIN_BRUSH_SIZE,
  CUTOUT_REFINE_ORIGINAL_PREVIEW_OPACITY,
  CUTOUT_REFINE_OUTSIDE_CLICK_TOLERANCE_PX,
  CUTOUT_REFINE_SURFACE_ATTRIBUTE,
  CutoutRefineEntryPoint,
  CutoutRefineExit,
} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine.types';
import {CutoutRefineTool} from '@PosterWhiteboard/classes/item-refinement.types';
import {RESELECT_ITEM_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';

/** A refine session's state: its strokes, whether "Reset all edits" put the background back, and whether anything changed yet. */
interface RefineSessionStep {
  strokes: ItemRefinementStrokeObject[];
  isBackgroundReset: boolean;
  hasEdits: boolean;
}

/** What a session needs once its images have downloaded; nothing is written to the controller until the session is known to still be live. */
interface LoadedRefineSession {
  sources: RefineSources;
  strokes: ItemRefinementStrokeObject[];
  masks: RefinementMasks;
}

/** The brush path being painted, in mask pixels; it becomes a stored stroke once the brush lifts or leaves the picture. */
interface PaintingStroke {
  tool: CutoutRefineTool;
  lineWidth: number;
  points: RefinementPoint[];
}

/** The images a refine session paints with, all at the item's loaded resolution. */
interface RefineSources {
  cutout: HTMLImageElement;
  original: HTMLImageElement;
  /** The item's crop window in cutout pixels; the whole cutout when the item is uncropped. */
  crop: CropData;
  cropRotation: number;
  /** The item's baked image size: the crop window plus any stroke border around it. */
  bakedWidth: number;
  bakedHeight: number;
  /** Where the crop window starts inside the baked image, pushed in by a stroke border. */
  borderInset: number;
  /** A mask, edge effect or stroke border is baked in, so the preview runs the item's own effect pipeline. */
  usesEffectPipeline: boolean;
}

/** What a composite of the session's strokes draws from, and the size it is drawn at. */
interface RefinementCompositeInput {
  cutout: HTMLImageElement;
  original: HTMLImageElement;
  masks: RefinementMasks;
  width: number;
  height: number;
}

/**
 * On-canvas "Refine cutout" mode for a canvas image, its background removed or not; "cutout" below means
 * whichever image the item currently shows (the removed-background cutout, or the plain image). The item is hidden and a
 * preview image takes its place: the refined cutout over a checkerboard, cropped as the item is. Erase
 * and Restore brushes paint two live masks in the cutout's pixel space and record each stroke; Apply
 * stores the strokes on the item as `refinement`, which is one undo step. A refinement
 * too big for the poster is flattened into a new uploaded image instead.
 *
 * The preview runs the item's own crop, mask, edge-effect and stroke-border pipeline, so it matches
 * what Apply will show.
 */
type CanvasSelectionChange = CanvasEvents['selection:created'] | CanvasEvents['selection:updated'];

export class PosterCutoutRefine {
  private activeItem: CutoutRefineTargetItem | null = null;
  private readonly analytics = new PosterCutoutRefineAnalytics();
  private sources: RefineSources | null = null;
  /**
   * Live erase/restore masks the preview composites. Painted at pointer precision as the brush moves; Undo
   * and Redo rebuild them by replaying {@link strokes}, which sit on the grid, so an edge can shift by a
   * fraction of a mask pixel then.
   */
  private masks: RefinementMasks | null = null;
  private strokes: ItemRefinementStrokeObject[] = [];
  private paintingStroke: PaintingStroke | null = null;
  /** Session states before each stroke or reset, newest last: the panel's Undo steps back through them. */
  private undoStack: RefineSessionStep[] = [];
  /** States undone in this session that Redo can bring back; any new stroke or reset clears them. */
  private redoStack: RefineSessionStep[] = [];
  /** The redo stack as it was before the current press painted, so a press dropped for a pinch gives it back. */
  private redoStackBeforePress: RefineSessionStep[] = [];
  /** Counts session starts, so a download that finishes after its session was cancelled cannot touch a newer one. */
  private loadingSessionId = 0;
  private hasEdits = false;
  /** "Reset all edits" also undoes background removal: the preview paints over the original, and Apply turns removal off. */
  private isBackgroundReset = false;
  /** Carries over between sessions, like the brush size, except that the Background Remover always opens on erase. */
  private tool = CutoutRefineTool.ERASE;
  private brushSize = CUTOUT_REFINE_DEFAULT_BRUSH_SIZE;
  private showOriginal = false;
  private isApplying = false;
  /** Opened from the Eraser tile, a reset only clears brush strokes; opened from Remove Background it also puts the background back. */
  private entryPoint = CutoutRefineEntryPoint.ERASER;
  private previewCanvas: HTMLCanvasElement | null = null;
  private previewImage: FabricImage | null = null;
  /** A DOM circle rather than a canvas object, so it follows the pointer over the grey whiteboard around the page too. */
  private brushCursorElement: HTMLDivElement | null = null;
  private previousWhiteboardCursor = '';
  private hiddenSourceObject: FabricObject | null = null;
  /** Where the current stroke segment last painted; null between segments (off the picture, or no press). */
  private lastStrokePoint: Point | null = null;
  /** The primary button (or a finger) went down on the whiteboard and is still down: moving over the picture paints. */
  private isPointerHeld = false;
  /** Whether this press has painted yet: its first paint records one undo step for the whole press. */
  private hasPressPainted = false;
  /** Set once the session's images have loaded and its tools are shown; cleared on exit. */
  private isSessionOpen = false;
  private pendingRenderFrame: number | null = null;
  /** The effect pipeline is async; one run at a time, with at most one more queued for the latest strokes. */
  private isEffectRenderRunning = false;
  private isEffectRenderQueued = false;
  /** The last picture painted, so a zoom change can repaint the checkerboard without re-running the effect pipeline. */
  private lastDrawPicture: ((context: CanvasRenderingContext2D) => void) | null = null;
  /** Screen pixels per cutout pixel at the last paint; the checkerboard is sized in screen pixels. */
  private lastPaintedScreenScale = 0;
  private previousCanvasSelection = true;
  private previousDefaultCursor = 'default';
  private previousHoverCursor = 'move';
  private previousShortcutScope: string = KeyboardShortcutScope.POSTER_EDITOR;
  /** Where a press landed outside refine, in client pixels; its release, if it has not travelled, is a click that leaves the mode. */
  private outsidePressClientPoint: Point | null = null;

  public isActive(): boolean {
    return this.activeItem !== null;
  }

  public getActiveItem(): CutoutRefineTargetItem | null {
    return this.activeItem;
  }

  /**
   * Whether the Eraser is offered for this item, on desktop and mobile alike: a canvas image whose source
   * supports background removal, background removed or not. Icons and drawings are vector or re-rendered
   * art, and page backgrounds are out of scope. An upload still in flight is offered too, and its tile stays
   * disabled until the file is stored (see {@link isRefinableItem}).
   */
  public isEraserOfferedFor(item: Item): item is CutoutRefineTargetItem {
    return item.isImage() && doesSupportRemoveBackground(item.imageSource) && !item.isBackground && !item.isIcon() && !item.isAiIcon() && !item.isDrawing();
  }

  /** An offered image whose file is stored: the session loads its sources from the file, which an upload in flight does not have yet. */
  public isRefinableItem(item: Item): item is CutoutRefineTargetItem {
    return this.isEraserOfferedFor(item) && !!item.hashedFilename;
  }

  /** Once its background is removed, a refinable image's Remove Background control opens the refiner, titled "Background Remover", instead of toggling removal off. */
  public doesRemoveBackgroundOpenRefiner(item: Item): boolean {
    return this.isRefinableItem(item) && item.removeBackground.isBackgroundRemoved && !item.removeBackground.isBackgroundBeingRemoved;
  }

  /** A refinable image that is not mid background removal, which is about to swap the image underneath. */
  public canRefine(item: CutoutRefineTargetItem): boolean {
    return this.isRefinableItem(item) && !item.removeBackground.isBackgroundBeingRemoved;
  }

  /**
   * The item is reserved, and the keyboard switched to refine's scope, before the downloads: a second request
   * cannot start a session mid-load, and poster shortcuts (undo, delete) cannot change the item meanwhile.
   * Nor can another on-canvas mode (crop) open over it: both freeze the pages, and the second would
   * overwrite the first's snapshot of what to unfreeze.
   */
  public async enterRefineMode({item, entryPoint, gaSource}: CutoutRefineOpenRequest): Promise<void> {
    if (item.page.poster.isOnCanvasEditModeActive() || !this.canRefine(item)) {
      return;
    }
    this.#reserveItem(item, entryPoint);
    const sessionId = this.loadingSessionId;
    const {hashedFilename} = item;

    const session = await this.#loadSession(item);
    if (sessionId === this.loadingSessionId) {
      getPosterUi().setCutoutRefineLoading(null);
    }
    if (!this.#isLiveSession(item, sessionId)) {
      return;
    }
    if (!session) {
      this.#releaseItem();
      this.#showErrorGrowl();
      return;
    }
    if (item.hashedFilename !== hashedFilename) {
      // The image was replaced while its sources downloaded: the session would paint over the wrong picture.
      this.#releaseItem();
      return;
    }
    if (item.page.poster.isPlaying()) {
      await item.page.poster.pause();
      if (!this.#isLiveSession(item, sessionId)) {
        return;
      }
    }

    this.#openSession(item, session);
    this.isSessionOpen = true;
    this.analytics.trackOpened(entryPoint, gaSource);
    getPosterUi().enterCutoutRefineMode({itemId: item.uid, entryPoint, ...this.#getSnapshot()});
  }

  /** Claims the item and the keyboard for the session about to load, under a new session id. */
  #reserveItem(item: CutoutRefineTargetItem, entryPoint: CutoutRefineEntryPoint): void {
    this.activeItem = item;
    this.entryPoint = entryPoint;
    this.loadingSessionId += 1;
    this.previousShortcutScope = hotkeys.getScope();
    hotkeys.setScope(KeyboardShortcutScope.CUTOUT_REFINE);
    getPosterUi().setCutoutRefineLoading({itemId: item.uid, entryPoint});
  }

  /** Gives the item and the keyboard back when the session could not load. */
  #releaseItem(): void {
    this.activeItem = null;
    hotkeys.setScope(this.previousShortcutScope);
  }

  /**
   * Puts the loaded session on the canvas in the item's place. The item stays selected, so its outline
   * frames what is being refined, but the pages are frozen and the controls overlay draws no move, resize
   * or rotate handles while refine is open.
   */
  #openSession(item: CutoutRefineTargetItem, session: LoadedRefineSession): void {
    const {page} = item;
    this.#startSession(session);
    this.#freezePages(page.poster);
    page.renderAlignmentGuidelines.suspend();
    const source = item.fabricObject;
    source.visible = false;
    this.hiddenSourceObject = source;

    this.#createCanvasObjects(page, source);
    this.#paintCurrentItemImage(item);
    this.#renderPreview();
    this.#listenForPointer();
    this.#showBrushCursorOverWhiteboard();
  }

  /** Captured on the window, so a press anywhere (a panel, the grey whiteboard) is seen before the canvas handles it. */
  #listenForPointer(): void {
    window.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    window.addEventListener('pointerup', this.onDocumentPointerUp, true);
    window.addEventListener('pointercancel', this.onDocumentPointerCancel, true);
    window.addEventListener('pointermove', this.onDocumentPointerMove, true);
  }

  #stopListeningForPointer(): void {
    window.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    window.removeEventListener('pointerup', this.onDocumentPointerUp, true);
    window.removeEventListener('pointercancel', this.onDocumentPointerCancel, true);
    window.removeEventListener('pointermove', this.onDocumentPointerMove, true);
  }

  public setTool(tool: CutoutRefineTool): void {
    this.tool = tool;
    this.#publishSnapshot();
  }

  public setBrushSize(brushSize: number): void {
    this.brushSize = clamp(brushSize, CUTOUT_REFINE_MIN_BRUSH_SIZE, CUTOUT_REFINE_MAX_BRUSH_SIZE);
    this.#publishSnapshot();
  }

  public setShowOriginal(showOriginal: boolean): void {
    this.showOriginal = showOriginal;
    if (showOriginal) {
      this.analytics.recordShowOriginal();
    }
    this.#scheduleRender();
    this.#publishSnapshot();
  }

  /**
   * Clears every erase and restore stroke. Opened from Remove Background ("Reset all edits") it also puts a
   * removed background back; opened from Eraser ("Reset eraser edits") it leaves the removal alone.
   * Takes effect on Apply.
   */
  public resetAllEdits(): void {
    if (!this.masks || !this.activeItem || this.isApplying) {
      return;
    }
    this.#commitPaintingStroke();
    this.#pushUndoStep();
    this.#trimUndoStack();
    this.analytics.recordReset();
    this.strokes = [];
    this.masks = createEmptyRefinementMasks(this.masks.eraseMask.width, this.masks.eraseMask.height);
    this.isBackgroundReset = this.#doesResetRestoreBackground() && this.activeItem.removeBackground.isBackgroundRemoved;
    this.hasEdits = true;
    this.#scheduleRender();
    this.#publishSnapshot();
  }

  /**
   * Leaves refine the way crop leaves on a click beside it: keeping the edits if there are any, otherwise
   * just closing. Before the sources have loaded there is nothing to keep, so this abandons the load.
   */
  public async leaveRefineMode(exit: CutoutRefineExit): Promise<void> {
    if (!this.isActive()) {
      return;
    }
    if (this.hasEdits) {
      await this.applyRefinement(exit);
      return;
    }
    this.cancelRefinement();
  }

  /** The editor's Undo while refining (navbar or Ctrl/Cmd+Z): steps back one erase/restore stroke or reset, not poster history. */
  public undoEdit(): void {
    if (this.isApplying) {
      return;
    }
    this.#breakPressForHistoryStep();
    const previousStep = this.undoStack.pop();
    const currentStep = this.#getCurrentStep();
    if (!previousStep || !currentStep) {
      return;
    }
    this.redoStack.push(currentStep);
    this.#restoreStep(previousStep);
  }

  public redoEdit(): void {
    if (this.isApplying) {
      return;
    }
    this.#breakPressForHistoryStep();
    const nextStep = this.redoStack.pop();
    const currentStep = this.#getCurrentStep();
    if (!nextStep || !currentStep) {
      return;
    }
    this.undoStack.push(currentStep);
    this.#restoreStep(nextStep);
  }

  /** Undo/redo during a held press: painting that continues afterwards starts a fresh undo step instead of extending the undone one. */
  #breakPressForHistoryStep(): void {
    this.#commitPaintingStroke();
    this.#keepPressPaint();
    this.hasPressPainted = false;
    this.lastStrokePoint = null;
  }

  #getCurrentStep(): RefineSessionStep | null {
    return this.masks ? {strokes: this.strokes, isBackgroundReset: this.isBackgroundReset, hasEdits: this.hasEdits} : null;
  }

  #restoreStep({strokes, isBackgroundReset, hasEdits}: RefineSessionStep): void {
    if (!this.masks) {
      return;
    }
    this.paintingStroke = null;
    this.strokes = strokes;
    this.masks = rasterizeRefinement(toRefinement(strokes), this.masks.eraseMask.width, this.masks.eraseMask.height);
    this.isBackgroundReset = isBackgroundReset;
    this.hasEdits = hasEdits;
    this.#scheduleRender();
    this.#publishSnapshot();
  }

  /**
   * Stores the strokes on the item, as one poster-history step. Nothing painted clears the refinement. The
   * preview stays up until the item has reloaded with the refinement, so the unrefined image never flashes
   * in between. The mode stays open with a growl if applying fails, so no strokes are lost. Poster teardown
   * can close the mode while the write is in flight; the session is gone then, so nothing is tracked or shown.
   */
  public async applyRefinement(exit: CutoutRefineExit): Promise<void> {
    const item = this.activeItem;
    if (!item || !this.masks || this.isApplying) {
      return;
    }
    this.#commitPaintingStroke();
    if (!this.hasEdits) {
      this.analytics.trackCancelled(false);
      this.#exit();
      return;
    }

    const fitsInPoster = countRefinementPoints(this.strokes) <= REFINEMENT_MAX_POINTS;
    if (!fitsInPoster && !canFlattenIntoUpload(item)) {
      this.#showTooManyEditsGrowl();
      return;
    }

    this.isApplying = true;
    const sessionId = this.loadingSessionId;
    this.#publishSnapshot();
    try {
      if (fitsInPoster) {
        await this.#storeStrokesOnItem(item);
      } else {
        await this.#flattenIntoNewUpload(item, sessionId);
      }
      if (!this.#isLiveSession(item, sessionId)) {
        return;
      }
      this.analytics.trackApplied(exit);
      this.#exit();
    } catch {
      if (!this.#isLiveSession(item, sessionId)) {
        return;
      }
      this.isApplying = false;
      this.#publishSnapshot();
      this.#showErrorGrowl();
    }
  }

  /**
   * Throws when the write did not land (an item mid re-init drops updates silently), so Apply keeps the
   * session open instead of discarding the strokes.
   */
  async #storeStrokesOnItem(item: CutoutRefineTargetItem): Promise<void> {
    const refinement = toRefinement(this.strokes);
    await item.updateFromObject(this.isBackgroundReset ? {refinement, removeBackground: {isBackgroundRemoved: false}} : {refinement});
    if (!isEqual(item.refinement.toObject(), refinement)) {
      throw new Error('The refinement was not stored on the item');
    }
  }

  /**
   * Bakes the refinement into a new full-size image in the user's uploads and points the item at it, with
   * no refinement left over. The original stays in their uploads, and Undo brings it back. Stops before the
   * upload, and again before the write, when the session was torn down while it waited: the editor that
   * asked for it is gone, so nothing should be uploaded for it or written to its item.
   */
  async #flattenIntoNewUpload(item: CutoutRefineTargetItem, sessionId: number): Promise<void> {
    const [cutout, original] = await Promise.all([loadImageWithRetriesAsync(item.getUnoptimizedHighresImageURL()), loadImageWithRetriesAsync(item.getHighResImageUrlWithBackground())]);
    if (!this.#isLiveSession(item, sessionId)) {
      return;
    }
    const {width, height} = this.isBackgroundReset ? original : cutout;
    const masks = rasterizeRefinement(toRefinement(this.strokes), width, height);
    const flattened = compositeRefinedCutout(this.#getCompositeSources({cutout, original, masks, width, height}));
    const uploadedImage = await uploadCanvasAsUserImage(flattened, `${item.hashedFilename ?? item.uid}${CUTOUT_REFINE_FLATTENED_FILE_SUFFIX}`);
    if (!this.#isLiveSession(item, sessionId)) {
      return;
    }
    await item.updateFromObject({
      hashedFilename: uploadedImage.filename,
      fileExtension: uploadedImage.ext,
      hasTransparency: true,
      isUpscaled: false,
      removeBackground: {isBackgroundRemoved: false},
      refinement: null,
    });
    if (item.hashedFilename !== uploadedImage.filename) {
      throw new Error('The flattened image was not applied to the item');
    }
  }

  /**
   * What a composite of the session's strokes draws from: the cutout, or the original when the reset also
   * puts the background back; the original is only composited when a restore stroke paints from it.
   */
  #getCompositeSources({cutout, original, masks, width, height}: RefinementCompositeInput): RefinedCutoutSources {
    return {
      cutout: this.isBackgroundReset ? original : cutout,
      original: this.#hasRestoreStrokes() ? original : null,
      ...masks,
      width,
      height,
    };
  }

  #hasRestoreStrokes(): boolean {
    return hasRestoreStrokes(this.strokes) || this.paintingStroke?.tool === CutoutRefineTool.RESTORE;
  }

  /** Poster teardown: leaves the mode even mid-apply, since nothing will be around to finish it. An open session counts as cancelled. */
  public dispose(): void {
    if (this.isSessionOpen) {
      this.analytics.trackCancelled(this.hasEdits);
    }
    this.#exit();
  }

  public cancelRefinement(): void {
    if (!this.isActive() || this.isApplying) {
      return;
    }
    if (this.isSessionOpen) {
      this.analytics.trackCancelled(this.hasEdits);
    }
    this.#exit();
  }

  /** Still the session this request started: not cancelled while it waited, and not replaced by a newer one. */
  #isLiveSession(item: CutoutRefineTargetItem, sessionId: number): boolean {
    return this.activeItem === item && this.loadingSessionId === sessionId;
  }

  /** The item already downloaded these files, so they normally come straight from the browser cache. Null when one fails to load. */
  async #loadSession(item: CutoutRefineTargetItem): Promise<LoadedRefineSession | null> {
    try {
      const [cutout, original] = await Promise.all([loadImageWithRetriesAsync(await item.getImageUrl()), loadImageWithRetriesAsync(item.getImageUrlWithBackground())]);
      const maskSize = getRefinementMaskSize(cutout.width, cutout.height);
      const storedRefinement = item.refinement.toObject();
      return {
        sources: {
          cutout,
          original,
          crop: this.#getCropInCutoutPixels(item, cutout),
          cropRotation: item.cropData.isCropped() ? item.cropData.rotation : 0,
          bakedWidth: item.fabricObject.width,
          bakedHeight: item.fabricObject.height,
          borderInset: item.getStrokeBorderInset(),
          usesEffectPipeline: item.hasEffectsBeyondCrop(),
        },
        strokes: storedRefinement !== null ? storedRefinement.strokes : [],
        masks: rasterizeRefinement(storedRefinement, maskSize.width, maskSize.height),
      };
    } catch {
      return null;
    }
  }

  #startSession({sources, strokes, masks}: LoadedRefineSession): void {
    this.sources = sources;
    this.strokes = strokes;
    this.masks = masks;
    this.paintingStroke = null;
    this.undoStack = [];
    this.redoStack = [];
    if (this.entryPoint === CutoutRefineEntryPoint.BACKGROUND_REMOVER) {
      this.tool = CutoutRefineTool.ERASE;
    }
    this.hasEdits = false;
    this.isBackgroundReset = false;
    this.isApplying = false;
  }

  /**
   * Warms the browser cache with the download a session would wait on: the pre-removal original. Called
   * when a refinable image is selected; failures are left for the session to report.
   */
  public prefetchSources(item: CutoutRefineTargetItem): void {
    if (this.isRefinableItem(item) && item.removeBackground.isBackgroundRemoved) {
      void loadImageWithRetriesAsync(item.getImageUrlWithBackground()).catch(noop);
    }
  }

  #getCropInCutoutPixels(item: CutoutRefineTargetItem, cutout: HTMLImageElement): CropData {
    if (!item.cropData.isCropped()) {
      return {x: 0, y: 0, width: cutout.width, height: cutout.height};
    }
    return item.cropData.getScaledCropData(cutout);
  }

  /**
   * Refine mode owns the whole editor, like crop mode: items on other pages must not be draggable while it is open.
   */
  #freezePages(poster: Poster): void {
    for (const page of Object.values(poster.pages.pagesHashMap)) {
      page.setOthersInteractive(false);
    }
  }

  #unfreezePages(poster: Poster): void {
    for (const page of Object.values(poster.pages.pagesHashMap)) {
      page.setOthersInteractive(true);
    }
  }

  #createCanvasObjects(page: Page, source: FabricObject): void {
    if (!this.sources) {
      return;
    }
    this.previewCanvas = document.createElement('canvas');
    this.previewCanvas.width = Math.max(1, Math.round(this.sources.bakedWidth));
    this.previewCanvas.height = Math.max(1, Math.round(this.sources.bakedHeight));

    const centre = source.getCenterPoint();
    this.previewImage = new FabricImage(this.previewCanvas, {
      originX: 'center',
      originY: 'center',
      left: centre.x,
      top: centre.y,
      angle: source.angle,
      flipX: source.flipX,
      flipY: source.flipY,
      scaleX: source.scaleX,
      scaleY: source.scaleY,
      skewX: source.skewX,
      skewY: source.skewY,
      opacity: source.opacity,
      selectable: false,
      evented: false,
      objectCaching: false,
      excludeFromExport: true,
    });
    if (source instanceof FabricImage) {
      this.previewImage.filters = [...source.filters];
    }
    page.fabricCanvas.add(this.previewImage);

    if (page.fabricCanvas instanceof Canvas) {
      this.previousCanvasSelection = page.fabricCanvas.selection;
      this.previousDefaultCursor = page.fabricCanvas.defaultCursor;
      this.previousHoverCursor = page.fabricCanvas.hoverCursor;
      page.fabricCanvas.selection = false;
      page.fabricCanvas.defaultCursor = 'none';
      page.fabricCanvas.hoverCursor = 'none';
      page.fabricCanvas.on('mouse:down', this.onCanvasMouseDown);
      page.fabricCanvas.on('mouse:move', this.onCanvasMouseMove);
      page.fabricCanvas.on('mouse:up', this.onCanvasMouseUp);
      page.fabricCanvas.on('mouse:out', this.onCanvasMouseOut);
      page.fabricCanvas.on('after:render', this.onCanvasAfterRender);
      page.fabricCanvas.on('selection:created', this.onCanvasSelectionChanged);
      page.fabricCanvas.on('selection:updated', this.onCanvasSelectionChanged);
    }
  }

  #teardownCanvasObjects(page: Page): void {
    if (this.previewImage) {
      page.fabricCanvas.remove(this.previewImage);
    }
    if (page.fabricCanvas instanceof Canvas) {
      page.fabricCanvas.off('mouse:down', this.onCanvasMouseDown);
      page.fabricCanvas.off('mouse:move', this.onCanvasMouseMove);
      page.fabricCanvas.off('mouse:up', this.onCanvasMouseUp);
      page.fabricCanvas.off('mouse:out', this.onCanvasMouseOut);
      page.fabricCanvas.off('after:render', this.onCanvasAfterRender);
      page.fabricCanvas.off('selection:created', this.onCanvasSelectionChanged);
      page.fabricCanvas.off('selection:updated', this.onCanvasSelectionChanged);
      page.fabricCanvas.selection = this.previousCanvasSelection;
      page.fabricCanvas.defaultCursor = this.previousDefaultCursor;
      page.fabricCanvas.hoverCursor = this.previousHoverCursor;
    }
    page.renderAlignmentGuidelines.resume();
    this.#unfreezePages(page.poster);
  }

  /** Another item was selected while the session was open; that selection stands when the session closes. */
  #hasSelectionMovedToAnotherItem(page: Page, item: CutoutRefineTargetItem): boolean {
    const activeObject = page.activeSelection.getActiveObject();
    return activeObject !== undefined && activeObject !== item.fabricObject && activeObject !== this.hiddenSourceObject;
  }

  /**
   * Another item was selected, which the layers panel can do while the mode is open: the session is over
   * for this one, keeping its edits the way a click beside it does. Only a selection landing on another item
   * counts. Every brush press clears the selection (the hidden item is no target for Fabric), and a refresh
   * discards and reselects the item itself; neither ends the session, and nothing moves it while Apply writes.
   */
  private readonly onCanvasSelectionChanged = (event: CanvasSelectionChange): void => {
    const item = this.activeItem;
    if (!item || event.e?.type === RESELECT_ITEM_EVENT_TYPE || this.isApplying || !this.#hasSelectionMovedToAnotherItem(item.page, item)) {
      return;
    }
    void this.leaveRefineMode(CutoutRefineExit.SELECTION_CHANGE);
  };

  #exit(): void {
    const item = this.activeItem;
    if (!item) {
      return;
    }
    const {page} = item;
    if (this.pendingRenderFrame !== null) {
      window.cancelAnimationFrame(this.pendingRenderFrame);
      this.pendingRenderFrame = null;
    }
    this.#teardownCanvasObjects(page);
    hotkeys.setScope(this.previousShortcutScope);
    this.#stopListeningForPointer();
    this.#removeBrushCursor();
    this.outsidePressClientPoint = null;

    if (this.hiddenSourceObject) {
      this.hiddenSourceObject.visible = true;
    }
    item.fabricObject.visible = true;
    if (!this.#hasSelectionMovedToAnotherItem(page, item)) {
      page.activeSelection.setActiveObject(item.fabricObject);
    }

    this.activeItem = null;
    this.sources = null;
    this.masks = null;
    this.strokes = [];
    this.paintingStroke = null;
    this.undoStack = [];
    this.redoStack = [];
    this.redoStackBeforePress = [];
    this.hasEdits = false;
    this.isBackgroundReset = false;
    this.isApplying = false;
    this.isEffectRenderQueued = false;
    this.lastDrawPicture = null;
    this.lastPaintedScreenScale = 0;
    this.showOriginal = false;
    this.previewCanvas = null;
    this.previewImage = null;
    this.hiddenSourceObject = null;
    this.lastStrokePoint = null;
    this.isPointerHeld = false;
    this.hasPressPainted = false;
    this.isSessionOpen = false;
    page.fabricCanvas.requestRenderAll();
    getPosterUi().exitCutoutRefineMode();
  }

  private readonly onCanvasMouseDown = (opt: TPointerEventInfo): void => {
    if (this.isApplying || !this.masks || isMultiTouch(opt.e)) {
      return;
    }
    const maskPoint = this.#sceneToMask(opt.scenePoint);
    if (!maskPoint) {
      this.outsidePressClientPoint = getPointerClientPoint(opt.e);
      return;
    }
    this.#paintTo(maskPoint);
  };

  /**
   * While the button is held, painting follows the pointer wherever the press began (beside the picture, on
   * the grey whiteboard) and pauses off the picture, resuming when the pointer comes back over it.
   */
  private readonly onCanvasMouseMove = (opt: TPointerEventInfo): void => {
    if (isMultiTouch(opt.e)) {
      this.#dropStrokeForPinch();
      return;
    }
    if (!this.isPointerHeld || this.isApplying) {
      return;
    }
    const maskPoint = this.#sceneToMask(opt.scenePoint);
    if (!maskPoint) {
      this.lastStrokePoint = null;
      return;
    }
    this.#paintTo(maskPoint);
  };

  /** A press anywhere outside refine's canvas and its own UI may be a click that leaves the mode, like crop. */
  private readonly onDocumentPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) {
      return;
    }
    this.isPointerHeld = this.#isOnWhiteboard(event.target);
    this.#placeBrushCursor(event);
    if (this.#isInsideRefineArea(event.target)) {
      return;
    }
    this.outsidePressClientPoint = getPointerClientPoint(event);
  };

  private readonly onDocumentPointerUp = (event: PointerEvent): void => {
    this.#endPress();
    this.#placeBrushCursor(event);
    const outsidePress = this.outsidePressClientPoint;
    this.outsidePressClientPoint = null;
    if (outsidePress && getPointerClientPoint(event).distanceFrom(outsidePress) <= CUTOUT_REFINE_OUTSIDE_CLICK_TOLERANCE_PX) {
      void this.leaveRefineMode(CutoutRefineExit.OUTSIDE_CLICK);
    }
  };

  /** The browser took the pointer away mid-gesture (a system gesture, a second finger): nothing was clicked. */
  private readonly onDocumentPointerCancel = (event: PointerEvent): void => {
    this.#endPress();
    this.#placeBrushCursor(event);
    this.outsidePressClientPoint = null;
  };

  /** The canvas and the grey area around the pages: a press there may become a stroke, unlike one on a panel or bar. */
  #isOnWhiteboard(target: EventTarget | null): boolean {
    const whiteboardContainer = this.#getWhiteboardContainer();
    return target instanceof Node && !!whiteboardContainer && whiteboardContainer.contains(target);
  }

  #getWhiteboardContainer(): HTMLElement | undefined {
    return window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0);
  }

  /** Refine's own page canvas decides per press (beyond the brush's reach counts as outside); its panels and bars never leave the mode. */
  #isInsideRefineArea(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    const canvas = this.activeItem?.page.fabricCanvas;
    const isRefinedCanvas = canvas instanceof Canvas && target === canvas.upperCanvasEl;
    return isRefinedCanvas || target.closest(`[${CUTOUT_REFINE_SURFACE_ATTRIBUTE}]`) !== null;
  }

  /** Keeps the checkerboard at a constant on-screen square size: after a zoom, the last picture is repainted over a resized board. */
  private readonly onCanvasAfterRender = (): void => {
    const drawPicture = this.lastDrawPicture;
    if (!drawPicture || this.#getScreenPixelsPerCutoutPixel() === this.lastPaintedScreenScale) {
      return;
    }
    this.#paintPreview(drawPicture);
  };

  private readonly onCanvasMouseUp = (): void => {
    this.#endPress();
  };

  /** Leaving the canvas only pauses the stroke; the press, and its undo step, carry on if the pointer comes back. */
  private readonly onCanvasMouseOut = (): void => {
    this.lastStrokePoint = null;
  };

  /**
   * A second finger means the user is pinching to zoom or pan, which the gesture layer handles. The first
   * finger's stroke was never meant as paint, so it is rolled back, and its undo step with it.
   */
  #dropStrokeForPinch(): void {
    const hasPressPainted = this.hasPressPainted;
    this.isPointerHeld = false;
    this.hasPressPainted = false;
    this.lastStrokePoint = null;
    if (!hasPressPainted) {
      return;
    }
    // Restored first: the step restore republishes the panel, and Redo must read the stack as it was before the press.
    this.redoStack = this.redoStackBeforePress;
    const strokeStart = this.undoStack.pop();
    if (strokeStart) {
      this.#restoreStep(strokeStart);
    }
  }

  /**
   * Paints from the last point of this segment, or a dot when a segment starts, which also starts a new
   * stroke. The press's first paint records its undo step.
   */
  #paintTo(maskPoint: Point): void {
    if (!this.hasPressPainted) {
      this.redoStackBeforePress = this.redoStack;
      this.#pushUndoStep();
      this.hasPressPainted = true;
    }
    if (this.lastStrokePoint === null || !this.paintingStroke) {
      this.#commitPaintingStroke();
      this.paintingStroke = {tool: this.tool, lineWidth: this.#getBrushDiameterInMaskPixels(), points: []};
    }
    this.paintingStroke.points.push({x: maskPoint.x, y: maskPoint.y});
    this.#paintSegment(this.paintingStroke, this.lastStrokePoint ?? maskPoint, maskPoint);
    this.lastStrokePoint = maskPoint;
  }

  /**
   * Once the press is kept (not dropped for a pinch): its paint counts for analytics, and its undo step
   * counts against the cap. Trimming the oldest step when the press began would leave it unrecoverable if
   * the step pushed last were then rolled back by a pinch.
   */
  #keepPressPaint(): void {
    if (!this.hasPressPainted) {
      return;
    }
    this.analytics.recordPaint(this.tool);
    this.#trimUndoStack();
  }

  /** Stores the stroke being painted with the session's strokes, simplified onto the refinement grid. */
  #commitPaintingStroke(): void {
    const {paintingStroke, masks} = this;
    this.paintingStroke = null;
    if (!paintingStroke || !masks || paintingStroke.points.length === 0) {
      return;
    }
    const maskSize = {width: masks.eraseMask.width, height: masks.eraseMask.height};
    this.strokes = [...this.strokes, createRefinementStroke({...paintingStroke, maskSize})];
  }

  /** The panel's Reset and Undo states only change once a press ends, so they are republished then rather than per segment. */
  #endPress(): void {
    this.#commitPaintingStroke();
    this.#keepPressPaint();
    const hasPressPainted = this.hasPressPainted;
    this.isPointerHeld = false;
    this.hasPressPainted = false;
    this.lastStrokePoint = null;
    if (hasPressPainted) {
      this.#publishSnapshot();
    }
  }

  #pushUndoStep(): void {
    if (!this.masks) {
      return;
    }
    this.undoStack.push({strokes: this.strokes, isBackgroundReset: this.isBackgroundReset, hasEdits: this.hasEdits});
    this.redoStack = [];
  }

  /** Keeps the newest steps up to the cap; the oldest state is the one dropped. */
  #trimUndoStack(): void {
    if (this.undoStack.length > CUTOUT_REFINE_MAX_UNDO_STEPS) {
      this.undoStack.shift();
    }
  }

  #paintSegment({tool, lineWidth}: PaintingStroke, from: Point, to: Point): void {
    if (!this.masks) {
      return;
    }
    paintRefinementSegment(this.masks, {tool, from, to, lineWidth});
    this.hasEdits = true;
    this.#scheduleRender();
  }

  /**
   * Maps a scene point to the mask: into the preview's own pixels, back through the item's crop
   * (rotation included), then scaled from the cutout to the mask. Null when the brush could not reach the picture.
   */
  #sceneToMask(scenePoint: Point): Point | null {
    if (!this.previewImage || !this.sources || !this.masks) {
      return null;
    }
    const {crop, cropRotation, cutout, bakedWidth, bakedHeight, borderInset} = this.sources;
    const localPoint = scenePoint.transform(util.invertTransform(this.previewImage.calcTransformMatrix()));
    const cropCentreInBaked = new Point(borderInset + crop.width / 2, borderInset + crop.height / 2);
    const fromCropCentre = localPoint.add(new Point(bakedWidth / 2, bakedHeight / 2)).subtract(cropCentreInBaked);
    const cutoutPoint = fromCropCentre.rotate(-degreesToRadians(cropRotation)).add(new Point(crop.x + crop.width / 2, crop.y + crop.height / 2));
    const maskScale = this.masks.eraseMask.width / cutout.width;
    const maskPoint = cutoutPoint.scalarMultiply(maskScale);
    return isWithinBrushReachOfMask(maskPoint, this.masks.eraseMask, this.#getBrushDiameterInMaskPixels() / 2) ? maskPoint : null;
  }

  #getScreenPixelsPerCutoutPixel(): number {
    if (!this.previewImage || !this.activeItem) {
      return 1;
    }
    return this.activeItem.page.fabricCanvas.getZoom() * Math.abs(this.previewImage.scaleX);
  }

  /**
   * The brush on screen, capped at the picture's long side as shown, so a stroke's stored width always fits
   * the grid (the cap ImageRefinementVO applies too) and the cursor shows what is actually painted.
   */
  #getBrushDiameterInScreenPixels(): number {
    if (!this.sources) {
      return this.brushSize;
    }
    const longSideOnScreen = Math.max(this.sources.cutout.width, this.sources.cutout.height) * this.#getScreenPixelsPerCutoutPixel();
    return Math.min(this.brushSize, longSideOnScreen);
  }

  #getBrushDiameterInMaskPixels(): number {
    if (!this.previewImage || !this.sources || !this.masks || !this.activeItem) {
      return 0;
    }
    const maskScale = this.masks.eraseMask.width / this.sources.cutout.width;
    return (this.#getBrushDiameterInScreenPixels() / this.#getScreenPixelsPerCutoutPixel()) * maskScale;
  }

  private readonly onDocumentPointerMove = (event: PointerEvent): void => {
    this.#placeBrushCursor(event);
  };

  /**
   * The brush circle follows a mouse anywhere over the whiteboard, page or grey surround. A finger has no
   * hover, so there it shows only while pressed, to show what the brush covers under the fingertip.
   */
  #placeBrushCursor(event: PointerEvent): void {
    const cursor = this.brushCursorElement;
    if (!cursor) {
      return;
    }
    const isHovering = event.pointerType === 'mouse' || this.isPointerHeld;
    if (!isHovering || !this.#isOnWhiteboard(event.target)) {
      cursor.style.display = 'none';
      return;
    }
    const diameter = this.#getBrushDiameterInScreenPixels();
    cursor.style.display = 'block';
    cursor.style.width = `${diameter}px`;
    cursor.style.height = `${diameter}px`;
    cursor.style.transform = `translate(${event.clientX - diameter / 2}px, ${event.clientY - diameter / 2}px)`;
  }

  /**
   * Lives inside the whiteboard so the floating rail and side panel draw over it at their edges; fixed
   * positioning keeps it out of the whiteboard's scrollable area.
   */
  #showBrushCursorOverWhiteboard(): void {
    const whiteboardContainer = this.#getWhiteboardContainer();
    if (!whiteboardContainer) {
      return;
    }
    const cursor = document.createElement('div');
    Object.assign(cursor.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      display: 'none',
      boxSizing: 'border-box',
      border: `${CUTOUT_REFINE_BRUSH_CURSOR_RING_PX}px solid ${CUTOUT_REFINE_BRUSH_CURSOR_RING_COLOR}`,
      boxShadow: `0 0 0 ${CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_PX}px ${CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_COLOR}, inset 0 0 0 ${CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_PX}px ${CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_COLOR}`,
      borderRadius: '50%',
      pointerEvents: 'none',
      zIndex: CUTOUT_REFINE_BRUSH_CURSOR_Z_INDEX,
    });
    whiteboardContainer.appendChild(cursor);
    this.brushCursorElement = cursor;
    this.previousWhiteboardCursor = whiteboardContainer.style.cursor;
    whiteboardContainer.style.cursor = 'none';
  }

  #removeBrushCursor(): void {
    if (!this.brushCursorElement) {
      return;
    }
    this.brushCursorElement.remove();
    this.brushCursorElement = null;
    const whiteboardContainer = this.#getWhiteboardContainer();
    if (whiteboardContainer) {
      whiteboardContainer.style.cursor = this.previousWhiteboardCursor;
    }
  }

  #scheduleRender(): void {
    if (this.pendingRenderFrame !== null) {
      return;
    }
    this.pendingRenderFrame = window.requestAnimationFrame(() => {
      this.pendingRenderFrame = null;
      this.#renderPreview();
    });
  }

  /**
   * The refined cutout as the item will show it. Without baked effects a crop is drawn directly, every
   * frame, from a composite at mask size on a reused canvas: the masks are capped where the cutout is not,
   * and the draw scales it up. With a mask, edge effect or stroke border the item's own pipeline bakes it,
   * asynchronously, at cutout size, since that pipeline sizes the border and edge from the item rather than
   * the image; that composite outlives the frame, so it gets its own canvas.
   */
  #renderPreview(): void {
    const {sources, masks} = this;
    if (!sources || !masks) {
      return;
    }
    if (sources.usesEffectPipeline) {
      const {width, height} = sources.cutout;
      void this.#renderPreviewThroughEffects(compositeRefinedCutout(this.#getCompositeSources({cutout: sources.cutout, original: sources.original, masks, width, height})));
      return;
    }
    const {width, height} = masks.eraseMask;
    const refined = compositeRefinedCutoutForPreview(this.#getCompositeSources({cutout: sources.cutout, original: sources.original, masks, width, height}));
    this.#paintPreview((context) => {
      drawCropped(context, refined, sources);
    });
  }

  async #renderPreviewThroughEffects(refined: HTMLCanvasElement): Promise<void> {
    const item = this.activeItem;
    if (!item) {
      return;
    }
    if (this.isEffectRenderRunning) {
      this.isEffectRenderQueued = true;
      return;
    }
    this.isEffectRenderRunning = true;
    try {
      await withCanvasAsImageAsync(refined, async (refinedImage) => {
        const baked = await item.applyCropAndEffectsToImage(refinedImage);
        if (this.activeItem === item) {
          this.#paintPreview((context) => {
            context.drawImage(baked, 0, 0);
          });
        }
      });
    } catch (error) {
      // The last good frame stays up; the next stroke retries, and Apply bakes through the real load path anyway.
      console.error('Refine cutout preview render failed', error);
    } finally {
      this.isEffectRenderRunning = false;
      if (this.isEffectRenderQueued) {
        this.isEffectRenderQueued = false;
        this.#scheduleRender();
      }
    }
  }

  /**
   * The item's own baked image is already exactly what the unchanged refinement looks like, so it fills
   * the preview at once while the first effect-pipeline render is still on its way.
   */
  #paintCurrentItemImage(item: CutoutRefineTargetItem): void {
    const currentImage = item.imageElement;
    this.#paintPreview((context) => {
      context.drawImage(currentImage, 0, 0);
    });
  }

  /** Checkerboard over the crop window, then the faded original when asked for, then the picture itself. */
  #paintPreview(drawPicture: (context: CanvasRenderingContext2D) => void): void {
    const {previewCanvas, previewImage, sources, activeItem} = this;
    const context = previewCanvas?.getContext('2d');
    if (!previewCanvas || !previewImage || !sources || !activeItem || !context) {
      return;
    }
    this.lastDrawPicture = drawPicture;
    this.lastPaintedScreenScale = this.#getScreenPixelsPerCutoutPixel();
    context.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    drawCheckerboard(context, {
      left: sources.borderInset,
      top: sources.borderInset,
      width: sources.crop.width,
      height: sources.crop.height,
      squareSize: CUTOUT_REFINE_CHECKER_SQUARE_SCREEN_PX / this.lastPaintedScreenScale,
    });
    if (this.showOriginal) {
      context.globalAlpha = CUTOUT_REFINE_ORIGINAL_PREVIEW_OPACITY;
      drawCropped(context, sources.original, sources);
      context.globalAlpha = 1;
    }
    drawPicture(context);
    if (previewImage.filters.length > 0) {
      previewImage.applyFilters();
    }
    previewImage.dirty = true;
    activeItem.page.fabricCanvas.requestRenderAll();
  }

  #getSnapshot(): CutoutRefineModeSnapshot {
    return {
      tool: this.tool,
      brushSize: this.brushSize,
      showOriginal: this.showOriginal,
      canResetAllEdits: this.#canResetAllEdits(),
      canUndo: !this.isApplying && this.undoStack.length > 0,
      canRedo: !this.isApplying && this.redoStack.length > 0,
      isApplying: this.isApplying,
    };
  }

  #canResetAllEdits(): boolean {
    const hasPaint = this.strokes.length > 0 || this.paintingStroke !== null;
    const hasRemovedBackground = this.activeItem !== null && this.activeItem.removeBackground.isBackgroundRemoved && !this.isBackgroundReset;
    return hasPaint || (this.#doesResetRestoreBackground() && hasRemovedBackground);
  }

  #doesResetRestoreBackground(): boolean {
    return this.entryPoint === CutoutRefineEntryPoint.BACKGROUND_REMOVER;
  }

  #publishSnapshot(): void {
    if (this.isActive()) {
      getPosterUi().updateCutoutRefineMode(this.#getSnapshot());
    }
  }

  #showTooManyEditsGrowl(): void {
    getPosterUi().showMessageGrowl({
      text: window.i18next.t('pmwjs_cutout_refine_too_many_edits'),
      type: GROWL_TYPE.NEUTRAL,
      showCloseIcon: true,
    });
  }

  #showErrorGrowl(): void {
    getPosterUi().showMessageGrowl({
      text: window.i18next.t('pmwjs_something_went_wrong'),
      type: GROWL_TYPE.DANGER,
      showCloseIcon: true,
    });
  }
}

const toRefinement = (strokes: ItemRefinementStrokeObject[]): ItemRefinementObject | null => {
  return strokes.length > 0 ? {strokes} : null;
};

/** Only the user's own uploads are flattened: baking a stock image into an upload would strip its licence. */
const canFlattenIntoUpload = (item: CutoutRefineTargetItem): boolean => {
  return isUserUploadedImageSource(item.imageSource);
};

const isTouch = (event: TPointerEventInfo['e']): event is TouchEvent => {
  return typeof TouchEvent !== 'undefined' && event instanceof TouchEvent;
};

const isMultiTouch = (event: TPointerEventInfo['e']): boolean => {
  return isTouch(event) && event.touches.length > 1;
};

/** A brush centred just off the picture still paints its edge, so the bounds are widened by the brush radius. */
const isWithinBrushReachOfMask = (point: Point, mask: HTMLCanvasElement, brushRadius: number): boolean => {
  return point.x >= -brushRadius && point.y >= -brushRadius && point.x <= mask.width + brushRadius && point.y <= mask.height + brushRadius;
};

/** Draws a full-cutout-sized image through the item's crop window, the same way ItemCrop bakes a crop, inside any stroke border. */
const drawCropped = (context: CanvasRenderingContext2D, image: CanvasImageSource, {crop, cropRotation, cutout, borderInset}: RefineSources): void => {
  context.save();
  context.translate(borderInset + crop.width / 2, borderInset + crop.height / 2);
  context.rotate(degreesToRadians(cropRotation));
  context.drawImage(image, -(crop.x + crop.width / 2), -(crop.y + crop.height / 2), cutout.width, cutout.height);
  context.restore();
};

interface CheckerboardArea {
  left: number;
  top: number;
  width: number;
  height: number;
  /** In preview-canvas pixels, so the squares land at a fixed on-screen size. */
  squareSize: number;
}

/**
 * The same board as the `transparency-checkerboard` SCSS mixin: one 2x2-square tile, white with its top-right and
 * bottom-left squares darkened, repeated as a pattern so a deep zoom costs one fill rather than a square per draw.
 */
const drawCheckerboard = (context: CanvasRenderingContext2D, {left, top, width, height, squareSize}: CheckerboardArea): void => {
  const pattern = context.createPattern(getCheckerboardTile(), 'repeat');
  if (!pattern) {
    return;
  }
  pattern.setTransform(new DOMMatrix().scaleSelf(squareSize / CUTOUT_REFINE_CHECKER_SQUARE_SCREEN_PX));
  context.save();
  context.translate(left, top);
  context.fillStyle = pattern;
  context.fillRect(0, 0, width, height);
  context.restore();
};

let checkerboardTile: HTMLCanvasElement | null = null;

const getCheckerboardTile = (): HTMLCanvasElement => {
  if (checkerboardTile) {
    return checkerboardTile;
  }
  const square = CUTOUT_REFINE_CHECKER_SQUARE_SCREEN_PX;
  const tile = document.createElement('canvas');
  tile.width = square * 2;
  tile.height = square * 2;
  const context = tile.getContext('2d');
  if (context) {
    context.fillStyle = CUTOUT_REFINE_CHECKER_LIGHT;
    context.fillRect(0, 0, tile.width, tile.height);
    context.fillStyle = CUTOUT_REFINE_CHECKER_DARK;
    context.fillRect(square, 0, square, square);
    context.fillRect(0, square, square, square);
  }
  checkerboardTile = tile;
  return tile;
};
